import { AlertTriangle, BadgeCheck, ShieldCheck, Trash2 } from 'lucide-react';
import { useState } from 'react';

import { mensagemDeErro } from '@/lib/erros';
import { formatarData, formatarDataHora, formatarDocumento } from '@/lib/formato';
import { situacaoCertificado } from '@/lib/situacao-certificado';
import { EstadoVazio } from '@/ui/components/estado-vazio';
import { CamposDetalhe } from '@/ui/patterns/campos-detalhe';
import { SecaoFormulario } from '@/ui/patterns/secao-formulario';
import { toast } from '@/ui/primitives/aviso';
import { Badge } from '@/ui/primitives/badge';
import { Button } from '@/ui/primitives/button';
import { Dialog } from '@/ui/primitives/dialog';
import { type CertificadoResumo, type EmpresaDetalhe } from '@fiscoai/contracts';

import { useRemoverCertificado } from './certificado/api';
import { FormularioEnvioCertificado } from './certificado/formulario-envio';

function Validade({ c }: { c: CertificadoResumo }) {
  const s = situacaoCertificado(c.validoAte);
  return (
    <div className="flex flex-wrap items-end justify-between gap-3 rounded-cartao border border-borda bg-superficie-afundada p-4">
      <div className="grid gap-0.5">
        <span className="text-sm text-texto-suave">Válido até</span>
        <span className="text-2xl font-semibold tabular">{formatarData(c.validoAte)}</span>
      </div>
      <Badge tom={s.tom}>{s.rotulo}</Badge>
    </div>
  );
}

function Avisos({ avisos }: { avisos: readonly string[] }) {
  if (avisos.length === 0) return null;
  return (
    <ul className="grid gap-1.5">
      {avisos.map((a) => (
        <li key={a} className="flex items-start gap-2 text-sm text-alerta">
          <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden />
          {a}
        </li>
      ))}
    </ul>
  );
}

/** Depois da primeira consulta respondida, a SEFAZ já conferiu a cadeia ICP-Brasil. */
function AceitoSefaz({ em }: { em: string | null }) {
  if (!em) return null;
  return (
    <p className="flex items-center gap-2 text-sm text-sucesso">
      <BadgeCheck className="size-4 shrink-0" aria-hidden />
      Aceito pela SEFAZ em {formatarDataHora(em)}
    </p>
  );
}

function Dados({ c }: { c: CertificadoResumo }) {
  return (
    <CamposDetalhe
      campos={[
        { rotulo: 'Titular', valor: c.titular, largo: true },
        { rotulo: 'CNPJ', valor: formatarDocumento(c.documento) },
        { rotulo: 'Emissor', valor: c.emissor ?? '—' },
        { rotulo: 'Válido desde', valor: c.validoDe ? formatarData(c.validoDe) : '—' },
        {
          rotulo: 'Número de série',
          valor: <span className="font-mono">{c.numeroSerie ?? '—'}</span>,
        },
        {
          rotulo: 'Impressão digital (SHA-256)',
          valor: <span className="font-mono text-xs break-all">{c.thumbprint}</span>,
          largo: true,
        },
        {
          rotulo: 'Enviado',
          valor: c.enviadoEm
            ? `${formatarDataHora(c.enviadoEm)}${c.enviadoPor ? ` por ${c.enviadoPor.nome}` : ''}`
            : '—',
          largo: true,
        },
      ]}
    />
  );
}

function RemoverCertificado({ empresaId }: { empresaId: string }) {
  const remover = useRemoverCertificado(empresaId);
  const [aberto, setAberto] = useState(false);
  return (
    <>
      <Button
        variante="fantasma"
        onClick={() => {
          setAberto(true);
        }}
      >
        <Trash2 aria-hidden /> Remover
      </Button>
      <Dialog
        aberto={aberto}
        aoMudar={setAberto}
        titulo="Remover o certificado?"
        descricao="O arquivo e a senha cifrados são apagados e a captura automática da SEFAZ para até um novo envio."
        acoes={
          <>
            <Button
              variante="fantasma"
              onClick={() => {
                setAberto(false);
              }}
            >
              Cancelar
            </Button>
            <Button
              variante="perigo"
              carregando={remover.isPending}
              onClick={() => {
                remover.mutate(undefined, {
                  onSuccess: () => {
                    toast.success('Certificado removido');
                    setAberto(false);
                  },
                  onError: (erro) => toast.error(mensagemDeErro(erro)),
                });
              }}
            >
              Remover certificado
            </Button>
          </>
        }
      >
        {null}
      </Dialog>
    </>
  );
}

/**
 * Certificado A1 (ADR 0018, spec 0005): só de escrita. Mostra a validade em destaque e os dados
 * públicos; quem tem `empresa.certificado.gerenciar` envia, troca ou remove.
 */
export function AbaCertificado({
  empresa,
  podeGerenciar,
}: {
  empresa: EmpresaDetalhe;
  podeGerenciar: boolean;
}) {
  const c = empresa.certificado;
  const [trocando, setTrocando] = useState(false);
  const descricao =
    'Com o A1 da empresa, o FiscoAI busca as notas direto na SEFAZ. Guardado cifrado: depois do envio, nenhuma tela mostra nem permite baixar o arquivo e a senha.';
  if (!c || trocando)
    return (
      <SecaoFormulario titulo="Certificado digital A1" descricao={descricao}>
        {podeGerenciar ? (
          <FormularioEnvioCertificado
            empresaId={empresa.id}
            troca={c !== null}
            {...(trocando
              ? {
                  aoConcluir: () => {
                    setTrocando(false);
                  },
                  aoCancelar: () => {
                    setTrocando(false);
                  },
                }
              : {})}
          />
        ) : (
          <EstadoVazio
            icone={<ShieldCheck />}
            titulo="Certificado ainda não enviado"
            texto="Quem gerencia o certificado da empresa (fiscal líder ou administrador) faz o envio aqui."
          />
        )}
      </SecaoFormulario>
    );
  return (
    <SecaoFormulario
      titulo="Certificado digital A1"
      descricao={descricao}
      acoes={
        podeGerenciar && (
          <>
            <RemoverCertificado empresaId={empresa.id} />
            <Button
              onClick={() => {
                setTrocando(true);
              }}
            >
              Substituir certificado
            </Button>
          </>
        )
      }
    >
      <Validade c={c} />
      <AceitoSefaz em={c.aceitoSefazEm} />
      <Avisos avisos={c.avisos} />
      <Dados c={c} />
    </SecaoFormulario>
  );
}
